import { Component, computed, input, ChangeDetectionStrategy } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { de } from 'date-fns/locale';
import { ScheduleTranslatorDebuggerTimestep } from '../../api/models/schedule-translator-debugger-timestep';

/** EUR/Wh → ct/kWh */
const EUR_PER_WH_TO_CT_PER_KWH = 100_000;

type PointValue = (step: ScheduleTranslatorDebuggerTimestep) => number | undefined;

interface Series {
  label: string;
  color: string;
  axis: 'power' | 'soc' | 'price';
  value: PointValue;
  dashed?: boolean;
  hidden?: boolean;
}

/**
 * Die Zeitschritte einer Optimierer-Lösung als Stufenlinien: Flüsse in kW (Ertrag und Last aus Wh je
 * Zeitschritt umgerechnet), SoC in kWh und die Preise in ct/kWh, je auf eigener Achse.
 */
@Component({
  selector: 'app-solution-chart',
  standalone: true,
  imports: [BaseChartDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="relative h-[24rem] w-full md:h-[30rem]">
      <canvas baseChart type="line" [data]="chartData()" [options]="chartOptions"></canvas>
    </div>
  `,
})
export class SolutionChartComponent {
  readonly timesteps = input<ScheduleTranslatorDebuggerTimestep[]>([]);

  /** Länge eines Zeitschritts in Stunden, aus dem kleinsten Abstand der Zeitstempel; 15 min, wenn unbekannt. */
  private readonly stepHours = computed(() => {
    const times = this.timesteps()
      .map((step) => new Date(step.timestamp ?? '').getTime())
      .filter((time) => !Number.isNaN(time));
    let smallest = Number.POSITIVE_INFINITY;
    for (let i = 1; i < times.length; i++) {
      const gap = times[i] - times[i - 1];
      if (gap > 0 && gap < smallest) {
        smallest = gap;
      }
    }
    return Number.isFinite(smallest) ? smallest / 3_600_000 : 0.25;
  });

  readonly chartData = computed<ChartData<'line'>>(() => {
    const steps = this.timesteps();
    const hours = this.stepHours();
    const kw = (value: number | undefined) => (value == null ? undefined : value / 1000);
    const series: Series[] = [
      { label: 'Bezug', color: '#334155', axis: 'power', value: (s) => kw(s.consumption) },
      { label: 'Einspeisung', color: '#94a3b8', axis: 'power', value: (s) => kw(s.feedin) },
      { label: 'Laden', color: '#16a34a', axis: 'power', value: (s) => kw(s.charge) },
      { label: 'Entladen', color: '#dc2626', axis: 'power', value: (s) => kw(s.discharge) },
      { label: 'Abregelung', color: '#f59e0b', axis: 'power', value: (s) => kw(s.curtail), dashed: true },
      {
        label: 'Ertrag',
       
        color: '#eab308',
        axis: 'power',
        value: (s) => (s.yieldWh == null ? undefined : s.yieldWh / hours / 1000),
        hidden: true,
      },
      {
        label: 'Last',
       
        color: '#7c3aed',
        axis: 'power',
        value: (s) => (s.loadWh == null ? undefined : s.loadWh / hours / 1000),
        hidden: true,
      },
      { label: 'SoC', color: '#0284c7', axis: 'soc', value: (s) => kw(s.soc) },
      {
        label: 'Preis Bezug',
       
        color: '#be185d',
        axis: 'price',
        value: (s) => (s.priceConsumptionEurWh == null ? undefined : s.priceConsumptionEurWh * EUR_PER_WH_TO_CT_PER_KWH),
        dashed: true,
      },
      {
        label: 'Preis Einsp.',
       
        color: '#f472b6',
        axis: 'price',
        value: (s) => (s.priceFeedinEurWh == null ? undefined : s.priceFeedinEurWh * EUR_PER_WH_TO_CT_PER_KWH),
        dashed: true,
      },
    ];
    return {
      datasets: series.map((serie) => ({
        label: serie.label,
        data: steps.map((step) => ({ x: new Date(step.timestamp ?? '').getTime(), y: serie.value(step) ?? null })) as any,
        yAxisID: serie.axis,
        borderColor: serie.color,
        backgroundColor: serie.color,
        borderWidth: serie.axis === 'power' ? 1.5 : 2,
        borderDash: serie.dashed ? [5, 3] : undefined,
        pointRadius: 0,
        pointHoverRadius: 3,
        fill: false,
        // Ein Zeitschritt gilt bis zum nächsten.
        stepped: 'after' as const,
        hidden: serie.hidden,
      })),
    };
  });

  readonly chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (item: TooltipItem<'line'>) => {
            const unit = item.dataset.yAxisID === 'price' ? 'ct/kWh' : item.dataset.yAxisID === 'soc' ? 'kWh' : 'kW';
            const value = item.parsed.y;
            return `${item.dataset.label}: ${value == null ? '–' : value.toLocaleString('de-AT', { maximumFractionDigits: 2 })} ${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        time: { unit: 'hour', tooltipFormat: 'dd.MM. HH:mm', displayFormats: { hour: 'HH:mm' } },
        adapters: { date: { locale: de } },
        ticks: { autoSkip: true, maxRotation: 0 },
      },
      power: { type: 'linear', position: 'left', title: { display: true, text: 'kW' } },
      soc: { type: 'linear', position: 'right', title: { display: true, text: 'SoC kWh' }, grid: { drawOnChartArea: false }, min: 0 },
      price: { type: 'linear', position: 'right', title: { display: true, text: 'ct/kWh' }, grid: { drawOnChartArea: false } },
    },
  };
}
